import React from 'react';
import { getHalfTimeOddsUnderOver } from '../functions';

const to2  = (x) => (Number.isFinite(x) ? x.toFixed(2) : '-');
const to1  = (x) => (Number.isFinite(x) ? x.toFixed(1) : '-');
const isNum = (x) => Number.isFinite(x);

// Allowed FT OU lines: 0.5, 1.5, ... 16.5
const ALLOWED_LINES = Array.from({ length: 17 }, (_, i) => (i * 10 + 5) / 10);

// ✅ FIX: detect coded half-time names like "OU_H1" / "OU_H2" as well
const nameHasHT = (name) => {
  const n = String(name || '').toUpperCase();

  // explicit coded suffixes
  if (n.includes('_H1') || n.includes('_H2')) return true;

  // textual markers
  return /(^|\s)(H1|H2|1ST\s*HALF|2ND\s*HALF|HALF)/i.test(n);
};

const getLine = (m) => Number(m?.h ?? m?.l ?? m?.line);

const hasOUOpts = (o) =>
  Array.isArray(o) &&
  o.some(x => /^(O|OVER)$/i.test(String(x?.n ?? ''))) &&
  o.some(x => /^(U|UNDER)$/i.test(String(x?.n ?? '')));

const findOpt = (opts, names) => {
  if (!Array.isArray(opts)) return undefined;
  const set = names.map((s) => String(s).toUpperCase());
  return opts.find((o) => set.includes(String(o?.n ?? '').toUpperCase()));
};

// Build a map of FT OU markets keyed by normalized line (1dp)
const collectFTOverUnder = (odds) => {
  const map = new Map(); // line(1dp number) -> options array
  (Array.isArray(odds) ? odds : []).forEach((m) => {
    // ⛔ skip half-time / period markets
    if (nameHasHT(m?.n)) return;

    const ln = getLine(m);
    if (!isNum(ln)) return;
    if (!hasOUOpts(m?.o)) return;

    const norm = Number(ln.toFixed(1));   // normalize to 1dp
    if (!map.has(norm)) map.set(norm, m.o);
  });
  return map;
};

const OverUnder = ({ blocked, odds, totalGoals = 0 }) => {
  // keep geometry when blocked
  if (blocked && blocked !== 0) return <td className="opacity-90" colSpan="3" />;

  // ---- Half-time (only show if valid; never show 0.0) ----
  let htLineNum;
  let htOdds = [];
  try {
    const ht = getHalfTimeOddsUnderOver(odds);
    if (ht && typeof ht === 'object') {
      const ln = Number(ht.line);
      if (isNum(ln)) htLineNum = ln;
      if (Array.isArray(ht.wireOdds)) htOdds = ht.wireOdds;
    }
  } catch {}

  const htUnder = findOpt(htOdds, ['Under', 'U']);
  const htOver  = findOpt(htOdds, ['Over', 'O']);
  const htHasOdds = isNum(Number(htUnder?.v)) || isNum(Number(htOver?.v));
  const showHT = isNum(htLineNum) && htLineNum > 0 && htHasOdds; // ✅ no 0.0 phantom lines

  // ---- Full-time selection restricted to ALLOWED_LINES and strictly > totalGoals ----
  const ftMap = collectFTOverUnder(odds);
  const tg = Number(totalGoals || 0);
  const desiredFTLine = ALLOWED_LINES.find(l => l > tg && ftMap.has(l));

  if (!isNum(desiredFTLine)) {
    // No allowed FT line available above current total — keep layout stable
    return <td className="opacity-90" colSpan="3" />;
  }

  const ftLine = desiredFTLine;
  const ftOdds = ftMap.get(ftLine) || [];
  const ftUnder = findOpt(ftOdds, ['Under', 'U']);
  const ftOver  = findOpt(ftOdds, ['Over', 'O']);
  const hasFTOdds = isNum(Number(ftUnder?.v)) || isNum(Number(ftOver?.v));
  if (!hasFTOdds) return <td className="opacity-90" colSpan="3" />;

  return (
    <>
      {/* Line cell (FT top, HT bottom only if valid) */}
      <td className="opacity-90 text-center">
        <span className="text-warning">{to1(ftLine)}</span>
        <br />
        {showHT ? <span className="ht-odds">{to1(htLineNum)}</span> : null}
      </td>

      {/* Under */}
      <td className="opacity-90 text-center">
        <span>{to2(Number(ftUnder?.v))}</span>
        <br />
        {showHT ? <span className="ht-odds">{to2(Number(htUnder?.v))}</span> : null}
      </td>

      {/* Over */}
      <td className="opacity-90 text-center">
        <span>{to2(Number(ftOver?.v))}</span>
        <br />
        {showHT ? <span className="ht-odds">{to2(Number(htOver?.v))}</span> : null}
      </td>
    </>
  );
};

export default OverUnder;
